import React, { useContext, useMemo, useState } from 'react';
import { Box, Paper, PaperProps } from '@mui/material';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { observer } from 'mobx-react-lite';
import CustomTextField from 'shared/lib/components/controls/textFields/customTextField';
import { emptySelectItemId } from 'shared/lib/utils/globalUtils';
import { Override } from 'shared/types/override';
import { useTranslation } from 'react-i18next';
import ButtonsContainer from 'shared/lib/components/controls/buttons/buttonsContainer';
import SecondaryButton from 'shared/lib/components/controls/buttons/secondaryButton';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import PlacementSelection from 'widgets/selectPlacement/placementSelection';
import { InitialPlacement, PlacementParams } from 'entities/types';
import { TableLinksHelper } from 'entities/table/model/tableLinksHelper';

export default observer(function SaveBlock({
  initialSectionId,
  initialFolderId,
  initialName,
  onSubmit,
  onCancel,
  nameParams,
  isExternalDisabled,
  shouldHighlightValidation,
  shouldDropNameAfterSave = false,
  onSubmitValidationError,
  tableHelper,
  ...other
}: InitialPlacement &
  Override<
    PaperProps,
    {
      onSubmit: (params: PlacementParams) => Promise<any>;
      onCancel: () => void;
      onSubmitValidationError?: () => void;
      isExternalDisabled?: boolean;
      shouldHighlightValidation?: boolean;
      shouldDropNameAfterSave?: boolean;
      nameParams?: {
        label: string;
        initialValue: string;
      };
      tableHelper?: TableLinksHelper;
    }
  >) {
  const { t } = useTranslation();
  const {
    spacings: { sp },
  } = useContext(UiContext);
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState(nameParams?.initialValue || '');

  const [selectedFolderId, setSelectedFolderId] = useState<string>(
    initialFolderId || ''
  );
  const [selectedSectionId, setSelectedSectionId] = useState<string>(
    initialSectionId || ''
  );

  const isSaveError =
    selectedSectionId === emptySelectItemId ||
    (nameParams && name === '') ||
    isExternalDisabled;

  const isDisabled = selectedSectionId === emptySelectItemId;

  const isFormChanged = useMemo(() => {
    const areRowsChanged = tableHelper?.isChanged || false;
    const isNameChanged = name !== nameParams?.initialValue;
    const isFolderChanged = selectedFolderId !== initialFolderId;
    const isSectionChanged = selectedSectionId !== initialSectionId;

    return (
      isNameChanged || isFolderChanged || isSectionChanged || areRowsChanged
    );
  }, [
    tableHelper?.isChanged,
    name,
    nameParams?.initialValue,
    selectedFolderId,
    initialFolderId,
    selectedSectionId,
    initialSectionId,
  ]);

  const handleLoadingChange = (isLoading: boolean) => {
    setIsLoading(isLoading);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: sp.s80 }}>
      <Paper
        elevation={4}
        {...other}
        sx={{
          display: 'flex',
          position: 'relative',
          flexDirection: 'column',
          p: sp.s70,
          gap: sp.s60,
          overflow: 'hidden',
          ...other.sx,
        }}
      >
        {nameParams ? (
          <CustomTextField
            error={shouldHighlightValidation && name === ''}
            value={name}
            onChange={(e) => setName(e.target.value)}
            wrapperSxProps={{ width: 1 }}
            label={nameParams.label}
            placeholder={nameParams.label}
            isClearIcon={true}
          ></CustomTextField>
        ) : (
          ''
        )}

        <PlacementSelection
          initialSectionId={selectedSectionId}
          initialFolderId={selectedFolderId || ''}
          onChangeFolder={(folderId) => setSelectedFolderId(folderId || '')}
          onChangeSection={(sectionId) => setSelectedSectionId(sectionId)}
          labelProps={{
            textFolder: t('common.selectFolder'),
            textSection: t('common.selectSection'),
            typographyVariantSize: 'f16',
          }}
          onLoadingChange={handleLoadingChange}
        />
      </Paper>
      <ButtonsContainer>
        <SecondaryButton onClick={onCancel}>Отмена</SecondaryButton>
        <PrimaryButton
          disabled={!isFormChanged || isDisabled}
          loading={isLoading}
          onClick={async () => {
            if (isSaveError) {
              onSubmitValidationError && onSubmitValidationError();
              return;
            }
            setIsLoading(true);

            const params = {
              sectionId: selectedSectionId,
              folderId: selectedFolderId,
            };

            if (selectedFolderId !== emptySelectItemId) {
              params.folderId = selectedFolderId || '';
            }

            try {
              await onSubmit({ ...params, name });
              if (shouldDropNameAfterSave) {
                setName('');
              }
            } catch (e) {
              console.error(e);
            }

            setIsLoading(false);
          }}
        >
          Сохранить
        </PrimaryButton>
      </ButtonsContainer>
    </Box>
  );
});
